import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, PenNib, ChartLineUp, ChatCircle, Lightning, SignOut, List, X, ShieldCheck, Bell, User, CreditCard, Gear, FolderOpen } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { LowCreditBanner } from './LowCreditBanner';
import NotificationBell from './NotificationBell';

const API = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { id: 'today', label: 'My Plan', icon: Sun, path: '/dashboard' },
  { id: 'create', label: 'Make Content', icon: PenNib, path: '/dashboard/create' },
  { id: 'intelligence', label: 'Spy & Grow', icon: ChartLineUp, path: '/dashboard/intelligence' },
  { id: 'advisor', label: 'My Advisor', icon: ChatCircle, path: '/dashboard/advisor' },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [usage, setUsage] = useState(null);

  // close profile menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-profile-menu]')) setProfileMenuOpen(false);
    };
    if (profileMenuOpen) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [profileMenuOpen]);

  useEffect(() => {
    const fetchUsage = async () => {
      try { const res = await axios.get(`${API}/api/user/usage`); setUsage(res.data); } catch {}
    };
    fetchUsage();
    const interval = setInterval(fetchUsage, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const getActiveId = () => {
    const p = location.pathname;
    if (p.includes('intelligence') || p.includes('brand-analyzer') || p.includes('competitor') || p.includes('seo') || p.includes('ab-testing') || p.includes('campaign-analytics')) return 'intelligence';
    if (p.includes('advisor') || p.includes('marketing-coach')) return 'advisor';
    if (p.includes('create') || p.includes('studio') || p.includes('carousel') || p.includes('voiceover') || p.includes('email') || p.includes('meta-ads') || p.includes('ad-creator') || p.includes('flyer') || p.includes('character') || p.includes('launch-page') || p.includes('creator-influencer') || p.includes('auto-campaign') || p.includes('strategy') || p.includes('creative-studio') || p.includes('campaign/')) return 'create';
    return 'today';
  };

  const activeId = getActiveId();

  /* ---- Sidebar (desktop only) ---- */
  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-6">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ background: '#c8f060', color: '#080808' }}>S</div>
        <span className="text-base font-semibold tracking-tight" style={{ color: '#f5f0e8' }}>ScalitOS</span>
      </div>

      {/* 4 nav items */}
      <nav className="flex-1 px-3 space-y-1">
        {NAV_ITEMS.map(item => {
          const isActive = activeId === item.id;
          const Icon = item.icon;
          return (
            <Link key={item.id} to={item.path}
              data-testid={`nav-${item.id}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200"
              style={{
                background: isActive ? 'rgba(200,240,96,0.1)' : 'transparent',
                color: isActive ? '#c8f060' : 'rgba(245,240,232,0.4)',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Credits + Admin + Logout */}
      <div className="px-3 pb-5 space-y-2">
        {/* Assets shortcut — universal library */}
        <Link to="/dashboard/assets" data-testid="nav-assets" onClick={() => setMobileMenuOpen(false)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all hover:bg-white/[0.03]"
          style={{ color: location.pathname.includes('/assets') ? '#c8f060' : 'rgba(245,240,232,0.4)', background: location.pathname.includes('/assets') ? 'rgba(200,240,96,0.1)' : 'transparent' }}>
          <FolderOpen size={18} weight={location.pathname.includes('/assets') ? 'fill' : 'regular'} />
          <span className="text-sm font-medium">Assets</span>
        </Link>
        {user?.role === 'admin' && (
          <>
            <Link to="/dashboard/admin" data-testid="nav-admin"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all hover:bg-white/[0.03]"
              style={{ color: location.pathname.includes('/admin') ? '#c8f060' : 'rgba(245,240,232,0.4)', background: location.pathname.includes('/admin') ? 'rgba(200,240,96,0.1)' : 'transparent' }}>
              <ShieldCheck size={18} weight={location.pathname.includes('/admin') ? 'fill' : 'regular'} />
              <span className="text-sm font-medium">Admin</span>
            </Link>
            <Link to="/dashboard/admin/notifications" data-testid="nav-notifications"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all hover:bg-white/[0.03]"
              style={{ color: 'rgba(245,240,232,0.4)' }}>
              <Bell size={18} />
              <span className="text-sm font-medium">Notifications</span>
            </Link>
          </>
        )}
        {usage && (
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Lightning size={16} weight="fill" style={{ color: '#c8f060' }} />
            <span className="text-xs font-medium" style={{ color: 'rgba(245,240,232,0.5)' }}>Credits</span>
            <span className="ml-auto text-sm font-bold" style={{ color: '#c8f060' }}>{usage.credits?.total ?? usage.total ?? 0}</span>
          </div>
        )}
        <button onClick={handleLogout} data-testid="nav-logout"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl w-full transition-all hover:bg-white/[0.03]"
          style={{ color: 'rgba(245,240,232,0.3)' }}>
          <SignOut size={18} />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: '#080808', color: '#f5f0e8' }}>
      {/* Desktop sidebar — hidden on mobile */}
      <aside className="hidden lg:flex w-[200px] shrink-0 flex-col fixed left-0 top-0 bottom-0 z-40" style={{ background: '#0a0a0a', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
        <Sidebar />
      </aside>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 lg:hidden" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setMobileMenuOpen(false)} />
            <motion.aside initial={{ x: -240 }} animate={{ x: 0 }} exit={{ x: -240 }} transition={{ type: 'spring', damping: 30, stiffness: 300 }} className="fixed left-0 top-0 bottom-0 w-[200px] z-50 lg:hidden" style={{ background: '#0a0a0a' }}>
              <Sidebar />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-1 lg:ml-[200px] min-h-screen pb-20 lg:pb-0" style={{ overflowX: 'hidden', minWidth: 0 }}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 px-4 py-3 flex items-center justify-between gap-3" style={{ background: 'rgba(8,8,8,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <button className="lg:hidden p-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }} onClick={() => setMobileMenuOpen(true)}>
            <List size={18} style={{ color: '#f5f0e8' }} />
          </button>
          <div className="flex-1" />
          {usage && (
            <Link to="/dashboard/billing" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.12)' }}>
              <Lightning size={13} weight="fill" style={{ color: '#c8f060' }} />
              <span className="text-xs font-bold" style={{ color: '#c8f060' }}>{usage.credits?.total ?? usage.total ?? 0}</span>
            </Link>
          )}
          <NotificationBell />
          <div className="relative" data-profile-menu>
            <button
              onClick={() => setProfileMenuOpen(v => !v)}
              data-testid="profile-menu-trigger"
              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-all hover:ring-2"
              style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', boxShadow: profileMenuOpen ? '0 0 0 2px rgba(200,240,96,0.3)' : 'none' }}
            >
              {(user?.name || user?.email || 'U')[0].toUpperCase()}
            </button>
            <AnimatePresence>
              {profileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.96 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-10 w-56 rounded-xl overflow-hidden z-50"
                  style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}
                  data-testid="profile-menu"
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <div className="text-sm font-semibold truncate" style={{ color: '#f5f0e8' }}>{user?.name || 'Account'}</div>
                    <div className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>{user?.email}</div>
                    {user?.role === 'admin' && (
                      <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ background: 'rgba(200,240,96,0.12)', color: '#c8f060' }}>
                        <ShieldCheck size={10} weight="fill" /> Admin
                      </div>
                    )}
                  </div>
                  <div className="py-1">
                    <Link to="/dashboard/settings" onClick={() => setProfileMenuOpen(false)} data-testid="menu-profile"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-white/[0.04]"
                      style={{ color: 'rgba(245,240,232,0.85)' }}>
                      <User size={15} /> Profile & Settings
                    </Link>
                    <Link to="/dashboard/billing" onClick={() => setProfileMenuOpen(false)} data-testid="menu-billing"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-white/[0.04]"
                      style={{ color: 'rgba(245,240,232,0.85)' }}>
                      <CreditCard size={15} /> Billing & Credits
                    </Link>
                    {user?.role === 'admin' && (
                      <>
                        <div className="my-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                        <Link to="/dashboard/admin" onClick={() => setProfileMenuOpen(false)} data-testid="menu-admin"
                          className="flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-white/[0.04]"
                          style={{ color: '#c8f060' }}>
                          <ShieldCheck size={15} weight="fill" /> Admin Dashboard
                        </Link>
                        <Link to="/dashboard/admin/notifications" onClick={() => setProfileMenuOpen(false)} data-testid="menu-notifications"
                          className="flex items-center gap-2.5 px-4 py-2 text-sm transition-colors hover:bg-white/[0.04]"
                          style={{ color: 'rgba(245,240,232,0.85)' }}>
                          <Bell size={15} /> Notifications
                        </Link>
                      </>
                    )}
                    <div className="my-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    <button onClick={() => { setProfileMenuOpen(false); handleLogout(); }} data-testid="menu-logout"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm w-full text-left transition-colors hover:bg-white/[0.04]"
                      style={{ color: 'rgba(245,240,232,0.6)' }}>
                      <SignOut size={15} /> Sign out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Page content */}
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto">
          <LowCreditBanner credits={usage?.credits} tier={usage?.tier} />
          {children}
        </div>
      </main>

      {/* Bottom nav (mobile + tablet) */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden" style={{ background: 'rgba(8,8,8,0.95)', backdropFilter: 'blur(16px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-around py-2.5 px-2 max-w-md mx-auto">
          {NAV_ITEMS.map(item => {
            const isActive = activeId === item.id;
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => navigate(item.path)} data-testid={`bottom-${item.id}`}
                className="flex flex-col items-center gap-0.5 py-1.5 px-5 rounded-xl transition-all"
                style={{ background: isActive ? 'rgba(200,240,96,0.08)' : 'transparent' }}>
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} style={{ color: isActive ? '#c8f060' : 'rgba(245,240,232,0.3)' }} />
                <span className="text-[10px] font-semibold" style={{ color: isActive ? '#c8f060' : 'rgba(245,240,232,0.25)' }}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
